// The chat composer (F-AGENT PR-B): a textarea + send button + the 3
// quick-prompt chips. Rebuilt in Tailwind from the throwaway mockup
// `panes/PaneAgent` (which used raw `.tp-*`/.kbd CSS + inline styles); the
// quick prompts are lifted as i18n strings ("Citer un fait" / "Trouver un
// arrêt" / "Rédiger PCM"), never as hard-coded literals.
//
// Behaviour:
//   - Send on click, and on Cmd/Ctrl+Enter (a bare Enter inserts a newline —
//     a legal question is often multi-line).
//   - Disabled when no active case OR while empty/whitespace.
//
// Invariants:
//   W2 — zero Office.js. Sending goes through `useAgent.send` (the SSE port).
//   §9 — logical RTL utilities only; all strings via `t('agent.composer.*')`
//        / `t('agent.quickPrompts.*')`. The textarea is `text-start`.

import { useState } from 'react'
import { useTranslation } from 'react-i18next'

interface ComposerProps {
  /** True when a case is active — gates the composer (W: no case → disabled). */
  enabled: boolean
  /** Submit a turn. The pane wires this to `useAgent.send`. */
  onSend: (message: string) => void
}

const QUICK_PROMPT_KEYS = [
  'agent.quickPrompts.citeFact',
  'agent.quickPrompts.findRuling',
  'agent.quickPrompts.draftPcm',
] as const

export function Composer({ enabled, onSend }: ComposerProps): JSX.Element {
  const { t } = useTranslation()
  const [value, setValue] = useState('')

  const canSend = enabled && value.trim().length > 0

  const submit = (): void => {
    if (!canSend) return
    onSend(value)
    setValue('')
  }

  /** A quick-prompt chip seeds the textarea (does not auto-send — the lawyer
   *  refines before sending). */
  const onQuickPrompt = (label: string): void => {
    if (!enabled) return
    setValue((prev) => (prev.trim().length > 0 ? `${prev} ${label}` : label))
  }

  return (
    <div
      data-testid="agent-composer"
      className="border-t border-ink/10 bg-white px-2.5 py-2.5"
    >
      <div className="flex items-end gap-1.5 rounded-lg border border-ink/15 bg-ink-soft/[0.02] p-2">
        <textarea
          data-testid="agent-composer-input"
          dir="auto"
          rows={2}
          disabled={!enabled}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            // Cmd/Ctrl+Enter sends; a bare Enter keeps the newline.
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault()
              submit()
            }
          }}
          placeholder={t('agent.composer.placeholder')}
          className="min-h-[38px] flex-1 resize-none bg-transparent text-start text-xs text-ink outline-none placeholder:text-ink/35 disabled:cursor-not-allowed"
        />
        <button
          type="button"
          data-testid="agent-composer-send"
          onClick={submit}
          disabled={!canSend}
          aria-label={t('agent.composer.send')}
          title={t('agent.composer.sendHint')}
          className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-ink font-bold text-gold disabled:cursor-not-allowed disabled:opacity-40"
        >
          <span aria-hidden="true">↑</span>
        </button>
      </div>

      <div className="mt-1.5 flex flex-wrap gap-1">
        {QUICK_PROMPT_KEYS.map((key) => (
          <button
            key={key}
            type="button"
            data-testid={`agent-quick-prompt-${key}`}
            disabled={!enabled}
            onClick={() => onQuickPrompt(t(key))}
            className="rounded border border-ink/10 px-1.5 py-0.5 text-[9.5px] text-ink/60 hover:bg-ink/5 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {t(key)}
          </button>
        ))}
      </div>

      {!enabled && (
        <p
          data-testid="agent-composer-no-case-hint"
          className="mt-1.5 text-[10px] text-ink/45"
        >
          {t('agent.composer.noCaseHint')}
        </p>
      )}
    </div>
  )
}
